/**
 * v2 Mapper page — rewrite (Wave 4b).
 *
 * Layout:
 *   top bar (breadcrumb · profile picker · save / kebab)
 *   ┌───────────────────────────────────────────────┐
 *   │ unified workbook grid │ inspector (right)     │
 *   ├───────────────────────────────────────────────┤
 *   │ bottom bar — row limit + AI controls           │
 *   └───────────────────────────────────────────────┘
 *
 * Changes vs. the previous shell (driven by user feedback):
 *  - Every column in COLUMN_GROUPS is selectable AND editable, including
 *    "Product Category" (previous bug: when no rule existed, the inspector
 *    rendered an empty-state with no kind picker, so the user could "select"
 *    the column but not actually add a rule to it).
 *  - Sidebar has a sticky search and an "+ Add custom column" button so the
 *    user can target columns outside the canonical 69.
 *  - The central canvas renders resolved workbook output cells; source rows
 *    remain available to the inspector/autocomplete surfaces.
 *  - Template editor supports `{{` auto-close and live header autocomplete.
 *  - Bottom drawer is resizable + collapsible; height persists.
 *
 * API contract is unchanged (see /api/...).
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router";
import { ApiError } from "@/lib/api/client";
import {
  createProfile,
  getProfile,
  getWorkbook,
  getWorkbookSheet,
  listDictionaries,
  listProfiles,
  previewWorkbook,
  setWorkbookSheetBinding,
  updateProfile,
} from "@/lib/api/endpoints";
import type {
  Detection,
  ImageConfig,
  LookupDictionary,
  ParsedSheet,
  Profile,
  ProductType,
  Rule,
  SourceRow,
  TaxonomyConfig,
  VariantConfig,
  WorkbookOverlay,
} from "@/lib/mapper/types";
import type { WorkbookPreviewRow, WorkbookSheetCell } from "@/lib/api/types";
import { MapperSidebar } from "./MapperSidebar";
import { ModuleConfigDrawer } from "@/modules/image-fanout/ConfigDrawer.client";
import { ImagesEditor } from "@/modules/image-fanout/ImagesEditor.client";
import { SequencePanel } from "./SequencePanel";
import { TaxonomyPanel } from "./TaxonomyPanel";
import { LayeredVariantsPanel } from "@/modules/variants/LayeredVariantsPanel.client";
import { shopifyTarget } from "@/lib/mapper/targets";
import {
  SaveTemplateModal,
  type SaveTemplateMode,
} from "./SaveTemplateModal";
import type { FieldInputHandle } from "@/modules/_shared/FieldInput.client";
import { autoMapHeaders } from "@/lib/mapper/automap";
import { SHOPIFY_COLUMNS } from "@/lib/mapper/columns";
import { pickTemplateForDetection } from "@/lib/mapper/template-select";
import { DEFAULT_TEMPLATE_TYPE } from "@/lib/mapper/template-types";
import { GridBottomBar } from "./_components/GridBottomBar";
import { useExportJob } from "./_hooks/useExportJob";
import { useGridColumns } from "./_hooks/useGridColumns";
import { useOverlay } from "./_hooks/useOverlay";
import {
  FieldsPanel,
  FIELDS_PANEL_OPEN_WIDTH,
  FIELDS_PANEL_RAIL_WIDTH,
} from "./SourcePanel";
import { EditableSourceGrid } from "./EditableSourceGrid";
import { WorkbookGrid, type GridColumn, type GridRow } from "./WorkbookGrid";
import { SaveMenu } from "./_components/SaveMenu";
import { WarningsBar } from "./_components/WarningsBar";
import { AI_RULE_PLACEHOLDER } from "@/lib/mapper/mapping";
import { ImportDialog } from "@/pages/workbooks/detail/ImportDialog";
import { ReconfigureImportButton } from "./ReconfigureImportButton";
import {
  blankProfile,
  confidenceTone,
  readActiveCustomerId,
} from "./_lib/mapperPage.helpers";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PREVIEW_DEBOUNCE_MS = 300;
const PREVIEW_LIMIT_DEFAULT = 5;
const PREVIEW_LIMIT_MIN = 1;
const PREVIEW_LIMIT_MAX = 50;
const PREVIEW_LIMIT_STORAGE_KEY = "monjolika.mapper.previewLimit";

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

type WorkbookMeta = {
  id: string;
  originalName: string;
  originalFileKey?: string | null;
  sheets: ParsedSheet[];
  detected: Record<string, Detection>;
};

type BindingMeta = {
  id: string;
  workbookId: string;
  sheetName: string;
  profileId: string | null;
  profile?: {
    id: string;
    name: string;
    productType: ProductType;
    isDefault: boolean;
  } | null;
};

type ProfileSummary = {
  id: string;
  name: string;
  productType: ProductType;
  type: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  config: Profile;
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------


function stableSourceRowId(index: number): string {
  return `source:${index}`;
}

// Limit used when loading the FULL source sheet for the editable Source tab.
// Mirrors the old standalone /edit page. Sheets larger than this can't be saved
// from the editor (saving would truncate) — see EditableSourceGrid's guard.
const EDIT_SHEET_LIMIT = 500;

function sourceCellToGridCell(
  value: SourceRow[string] | undefined,
): WorkbookSheetCell {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" || typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function moduleKindForColumn(
  profile: Profile,
  overlay: WorkbookOverlay,
  column: string,
): GridColumn["moduleKind"] {
  const rule = profile.fields[column];
  if (rule) {
    switch (rule.kind) {
      case "source":
      case "static":
      case "template":
      case "computed":
      case "dictionary":
      case "expression":
      case "conditional":
      case "ai":
        return rule.kind;
    }
  }
  return overlay.addedColumns.some((manual) => manual.name === column)
    ? "manual"
    : undefined;
}

export function SheetPage() {
  const { id: workbookId = "", sheet: sheetName = "" } = useParams();
  const [searchParams] = useSearchParams();
  const {
    exportWorkbook,
    status: exportStatus,
    progress: exportProgress,
  } = useExportJob(workbookId);
  const {
    overlay,
    setOverride,
    resetCell,
    addRow,
    addColumn,
    removeRows,
    mergeGenerated,
    clearGenerated,
    setSheetProfile,
    flush: flushOverlay,
    saving: overlaySaving,
    ready: overlayReady,
  } = useOverlay(workbookId, sheetName);
  // Latest overlay for preview requests (sent along so the server previews
  // exactly what the grid holds, including edits not autosaved yet).
  const overlayRef = useRef(overlay);
  useLayoutEffect(() => {
    overlayRef.current = overlay;
  }, [overlay]);

  // ---- async data -------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [workbookMeta, setWorkbookMeta] = useState<WorkbookMeta | null>(null);
  const [, setBindings] = useState<BindingMeta[]>([]);
  const [sourceHeaders, setSourceHeaders] = useState<string[]>([]);
  const [sourceRows, setSourceRows] = useState<SourceRow[]>([]);
  const [sourceTotal, setSourceTotal] = useState(0);
  const [availableProfiles, setAvailableProfiles] = useState<ProfileSummary[]>(
    [],
  );

  // ---- profile state ----------------------------------------------------
  const [loadedProfileId, setLoadedProfileId] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile>(blankProfile("Generic"));
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [customColumns, setCustomColumns] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  // The sheet has its own saved rules ("Save workbook") instead of following
  // its template. Seeded from the overlay once it has loaded.
  const [sheetOwnRules, setSheetOwnRules] = useState(false);
  const [workbookSavedAt, setWorkbookSavedAt] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const appliedSheetRulesRef = useRef(false);
  const [variantsOpen, setVariantsOpen] = useState(false);
  const [imagesOpen, setImagesOpen] = useState(false);
  const [taxonomyOpen, setTaxonomyOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveModalMode, setSaveModalMode] =
    useState<SaveTemplateMode | null>(null);

  // ---- preview state ----------------------------------------------------
  const [previewRows, setPreviewRows] = useState<WorkbookPreviewRow[]>([]);
  const [previewColumns, setPreviewColumns] = useState<string[]>([]);
  const [previewWarnings, setPreviewWarnings] = useState<string[]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Problems from the last AI generation (missing key, provider errors…),
  // kept until the next generation so a plain refresh doesn't hide them.
  const [aiWarnings, setAIWarnings] = useState<string[]>([]);
  // Bumped to re-run the preview after the overlay changes in a way the
  // current rows don't reflect yet (e.g. stored AI output cleared).
  const [previewNonce, setPreviewNonce] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewLimit, setPreviewLimit] = useState<number>(PREVIEW_LIMIT_DEFAULT);
  // Whether the most recent preview response had AI rules skipped. Drives the
  // "N AI cells to generate" hint in the bottom bar.
  const [lastAISkipped, setLastAISkipped] = useState<boolean>(true);
  // Timestamp of the most recent "Generate AI" run (a preview with
  // skipAI=false). Surfaced as "N rows · 4s ago" beside the button.
  const [testedAt, setTestedAt] = useState<number | null>(null);
  const [testedCount, setTestedCount] = useState<number>(0);
  const [testingAI, setTestingAI] = useState(false);

  // ---- spreadsheet grid state -------------------------------------------
  // The grid is the main canvas now. It renders resolved output cells only;
  // source rows remain loaded for the inspector/autocomplete surfaces.
  const [playingColumn, setPlayingColumn] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Bottom tab: the output workbook grid vs the editable source rows.
  // Opens on the Source tab when navigated to with ?tab=source (the "Edit
  // sheet" entry uses this), otherwise defaults to the workbook output.
  const [bottomTab, setBottomTab] = useState<"workbook" | "source">(() =>
    searchParams.get("tab") === "source" ? "source" : "workbook",
  );

  // ---- editable Source tab (full-sheet load) ---------------------------
  // The bottom Source tab is EDITABLE and saves the whole sheet, so it must
  // load the FULL sheet — not the small preview sample fed to the rest of the
  // page. Loaded lazily the first time the Source tab is opened.
  const [editSheet, setEditSheet] = useState<{
    headers: string[];
    rows: WorkbookSheetCell[][];
    total: number;
  } | null>(null);
  const [editSheetLoading, setEditSheetLoading] = useState(false);
  const [editSheetError, setEditSheetError] = useState<string | null>(null);
  // Right-edge Fields panel (Source fields / Export fields), collapsible.
  const [fieldsPanelOpen, setFieldsPanelOpen] = useState(false);

  // ---- sequence panel ---------------------------------------------------
  // Slide-over for execution-order management. Closes when the sheet changes
  // or when the user picks a different profile.
  const [sequencePanelOpen, setSequencePanelOpen] = useState(false);

  // ---- dictionary map ---------------------------------------------------
  // Loaded once on mount (and re-loaded after an inline dictionary save);
  // keyed by slug so the inline preview can resolve Dictionary module rules
  // without hitting the server per-keystroke.
  const [dictionaryMap, setDictionaryMap] = useState<Record<string, LookupDictionary>>({});

  const loadDictionaries = useCallback(() => {
    listDictionaries()
      .then((rows) => {
        const map: Record<string, LookupDictionary> = {};
        for (const r of rows) {
          if (r.slug) map[r.slug] = { slug: r.slug, name: r.name, entries: r.entries ?? [] };
        }
        setDictionaryMap(map);
      })
      .catch(() => { setDictionaryMap({}); });
  }, []);

  useEffect(() => {
    loadDictionaries();
  }, [loadDictionaries]);

  // Hydrate previewLimit from localStorage on mount; persist on change.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(PREVIEW_LIMIT_STORAGE_KEY);
      if (!raw) return;
      const n = Number(raw);
      if (Number.isFinite(n)) {
        const clamped = Math.min(
          PREVIEW_LIMIT_MAX,
          Math.max(PREVIEW_LIMIT_MIN, Math.floor(n)),
        );
        setPreviewLimit(clamped);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // The overlay to send with preview requests: only once the saved one has
  // loaded (before that the server's copy is the truth).
  const overlayForRequest = useCallback(
    () => (overlayReady ? overlayRef.current : undefined),
    [overlayReady],
  );

  // "Generate AI on the first N rows" — runs every AI rule on a small slice.
  // The output is stored in the workbook (like values typed into a
  // spreadsheet), then the grid refreshes with all preview rows.
  const handleTestAI = useCallback(
    async (n: 1 | 2 | 3) => {
      if (testingAI) return;
      setTestingAI(true);
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const json = await previewWorkbook(workbookId, {
          sheet: sheetName,
          profileConfig: profile,
          limit: n,
          skipAI: false,
          overlay: overlayForRequest(),
        });
        mergeGenerated(json.generated ?? {});
        setAIWarnings(json.aiWarnings ?? []);
        setLastAISkipped(json.aiSkipped ?? false);
        setTestedAt(Date.now());
        setTestedCount(n);
        setPreviewNonce((value) => value + 1);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? `AI generation failed (${err.status}): ${err.message}`
            : err instanceof Error
              ? err.message
              : String(err);
        setPreviewError(message);
        setPreviewLoading(false);
      } finally {
        setTestingAI(false);
      }
    },
    [workbookId, sheetName, profile, testingAI, overlayForRequest, mergeGenerated],
  );

  // ▶ on an AI column header — generate that one column on the previewed
  // rows. Cells that already hold AI output keep it; when nothing in view is
  // pending the button regenerates the column instead (fresh answers). Either
  // way the output is stored in the workbook so generating another column
  // never resets this one.
  const handlePlayColumn = useCallback(
    async (col: string) => {
      if (playingColumn) return;
      const regenerate = !previewRows.some(
        (row) => row[col] === AI_RULE_PLACEHOLDER,
      );
      setPlayingColumn(col);
      setPreviewError(null);
      try {
        const json = await previewWorkbook(workbookId, {
          sheet: sheetName,
          profileConfig: profile,
          limit: previewLimit,
          skipAI: true,
          aiColumns: [col],
          regenerate,
          overlay: overlayForRequest(),
        });
        mergeGenerated(json.generated ?? {});
        setPreviewRows(json.rows ?? []);
        setPreviewColumns(json.columns ?? []);
        setPreviewWarnings([...new Set(json.warnings ?? [])]);
        setAIWarnings(json.aiWarnings ?? []);
        setLastAISkipped(json.aiSkipped ?? true);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? `Generate failed (${err.status}): ${err.message}`
            : err instanceof Error
              ? err.message
              : String(err);
        setPreviewError(message);
      } finally {
        setPlayingColumn(null);
      }
    },
    [
      workbookId,
      sheetName,
      profile,
      previewLimit,
      playingColumn,
      previewRows,
      overlayForRequest,
      mergeGenerated,
    ],
  );

  // Forget stored AI output for one cell (it goes back to "pending").
  const handleClearGenerated = useCallback(
    (rowId: string, column: string) => {
      clearGenerated([column], [rowId]);
      setPreviewNonce((value) => value + 1);
    },
    [clearGenerated],
  );

  const handleChangePreviewLimit = useCallback((next: number) => {
    const clamped = Math.min(
      PREVIEW_LIMIT_MAX,
      Math.max(PREVIEW_LIMIT_MIN, Math.floor(next)),
    );
    setPreviewLimit(clamped);
    try {
      window.localStorage.setItem(
        PREVIEW_LIMIT_STORAGE_KEY,
        String(clamped),
      );
    } catch {
      /* ignore */
    }
  }, []);

  // ---- per-(column,kind) memory ----------------------------------------
  const [kindMemory, setKindMemory] = useState<Record<string, Rule>>({});

  // ---- auto-map UX state -----------------------------------------------
  const [recentlyAutoMapped, setRecentlyAutoMapped] = useState<Set<string>>(
    () => new Set(),
  );
  const [autoMapNotice, setAutoMapNotice] = useState<string | null>(null);
  const autoMapHighlightTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const autoMapNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // ---- focused autocomplete (for "click source header to insert") ------
  const focusedAutocomplete = useRef<FieldInputHandle | null>(null);

  function registerFocusedAutocomplete(
    handle: FieldInputHandle | null,
  ) {
    focusedAutocomplete.current = handle;
  }

  const insertTokenIntoInspector = useCallback((token: string) => {
    const handle = focusedAutocomplete.current;
    if (!handle) return;
    handle.insertAtCaret(token);
    handle.focus();
  }, []);

  // ---- full-sheet loader for the editable Source tab -------------------
  const loadEditSheet = useCallback(async () => {
    setEditSheetLoading(true);
    setEditSheetError(null);
    try {
      const json = await getWorkbookSheet(workbookId, sheetName, {
        limit: EDIT_SHEET_LIMIT,
        offset: 0,
      });
      const headers = json.headers ?? [];
      const rows = (json.rows ?? []).map((row) =>
        headers.map((header) => sourceCellToGridCell(row[header])),
      );
      setEditSheet({ headers, rows, total: json.total ?? rows.length });
    } catch (err) {
      setEditSheetError(
        err instanceof ApiError
          ? `Could not load sheet (${err.status}): ${err.message}`
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setEditSheetLoading(false);
    }
  }, [workbookId, sheetName]);

  // Lazily load the full sheet the first time the Source tab is shown. Re-loads
  // when the sheet changes or an import bumps the reload nonce.
  useEffect(() => {
    if (bottomTab !== "source") return;
    if (editSheet || editSheetLoading) return;
    void loadEditSheet();
  }, [bottomTab, editSheet, editSheetLoading, loadEditSheet]);

  // Drop the cached full sheet when the sheet changes / source is re-imported so
  // it's re-fetched fresh on next open.
  useEffect(() => {
    setEditSheet(null);
    setEditSheetError(null);
  }, [sheetName, reloadNonce]);

  // After the source is saved from the editable Source tab, re-pull the source
  // and re-run the mapper preview so the Workbook tab reflects the edits.
  const handleSourceSaved = useCallback(() => {
    setReloadNonce((value) => value + 1);
  }, []);

  // ---- initial load -----------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      appliedSheetRulesRef.current = false;
      try {
        const activeCustomerId = readActiveCustomerId();
        const [wbJson, sheetJson, profilesJson] = await Promise.all([
          getWorkbook(workbookId),
          getWorkbookSheet(workbookId, sheetName, { limit: 100 }),
          listProfiles({ customerId: activeCustomerId }),
        ]);

        if (cancelled) return;

        setWorkbookMeta({
          id: wbJson.workbook.id,
          originalName: wbJson.workbook.originalName,
          originalFileKey: wbJson.workbook.originalFileKey,
          sheets: wbJson.workbook.sheets,
          detected: wbJson.workbook.detected ?? {},
        });
        setBindings(wbJson.bindings ?? []);
        setSourceHeaders(sheetJson.headers ?? []);
        setSourceRows(sheetJson.rows ?? []);
        setSourceTotal(sheetJson.total ?? 0);
        setAvailableProfiles(profilesJson ?? []);

        const binding = (wbJson.bindings ?? []).find(
          (b) => b.sheetName === sheetName,
        );
        const detected = wbJson.workbook.detected?.[sheetName];

        if (binding?.profileId) {
          try {
            const p = await getProfile(binding.profileId);
            if (cancelled) return;
            setLoadedProfileId(p.id);
            applyLoadedProfile(p.config);
          } catch {
            if (cancelled) return;
            setLoadedProfileId(null);
            setProfile(
              blankProfile(detected?.type ?? "Generic", sheetJson.headers ?? []),
            );
          }
        } else {
          const pick = detected
            ? pickTemplateForDetection(
                profilesJson.map((p) => ({
                  id: p.id,
                  name: p.name,
                  productType: p.productType,
                  isDefault: p.isDefault,
                })),
                detected,
              )
            : null;
          if (pick) {
            // pick came from profilesJson, which already holds the full config —
            // no need to refetch (remote DB is slow; avoid the extra round-trip).
            const full = profilesJson.find((p) => p.id === pick.id);
            if (!cancelled) {
              if (full) {
                setLoadedProfileId(full.id);
                applyLoadedProfile(full.config);
              } else {
                setLoadedProfileId(null);
                setProfile(
                  blankProfile(
                    detected?.type ?? "Generic",
                    sheetJson.headers ?? [],
                  ),
                );
              }
            }
          } else if (!cancelled) {
            setLoadedProfileId(null);
            setProfile(
              blankProfile(detected?.type ?? "Generic", sheetJson.headers ?? []),
            );
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [workbookId, sheetName, reloadNonce]);

  /** When a profile is loaded, extract any non-canonical column names as customColumns. */
  function applyLoadedProfile(p: Profile) {
    setProfile(p);
    // Heuristic: any field key not in the canonical 69 is a custom column.
    // We don't have COLUMN_GROUPS imported here (sidebar owns that), so we
    // simply preserve any custom additions across loads by stashing keys that
    // aren't already in `customColumns`. The sidebar will dedupe.
    import("@/lib/mapper/columns").then(({ COLUMN_GROUPS }) => {
      const canonical = new Set<string>(
        COLUMN_GROUPS.flatMap((g) => g.columns as readonly string[]),
      );
      const customs = Object.keys(p.fields ?? {}).filter(
        (k) => !canonical.has(k),
      );
      setCustomColumns(customs);
    });
  }

  // Once both the template and the saved overlay are in, a sheet with its own
  // saved rules ("Save workbook") opens with those rules — like reopening a
  // saved spreadsheet.
  useEffect(() => {
    if (loading || !overlayReady || appliedSheetRulesRef.current) return;
    appliedSheetRulesRef.current = true;
    const own = overlayRef.current.profile;
    setSheetOwnRules(!!own);
    if (own) applyLoadedProfile(own);
  }, [loading, overlayReady]);

  // Keep unsaved rule changes (and AI output still being saved) from being
  // lost to an accidental tab close.
  useEffect(() => {
    if (!dirty && !overlaySaving) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty, overlaySaving]);

  // ---- preview fetcher (debounced) -------------------------------------
  useEffect(() => {
    if (loading || !overlayReady) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);

    const handle = setTimeout(async () => {
      try {
        const json = await previewWorkbook(workbookId, {
          sheet: sheetName,
          profileConfig: profile,
          limit: previewLimit,
          // Auto-refresh previews never spend AI tokens; AI output already
          // generated comes back from the workbook overlay. Only ▶ / Generate
          // AI call the provider. Exports reuse stored output and generate
          // the rest.
          skipAI: true,
          overlay: overlayForRequest(),
        });
        if (cancelled) return;
        setPreviewRows(json.rows ?? []);
        setPreviewColumns(json.columns ?? []);
        setPreviewWarnings([...new Set(json.warnings ?? [])]);
        // Auto-refresh always sends skipAI=true, but trust the server's flag
        // (it's the source of truth — older servers may not echo it).
        setLastAISkipped(json.aiSkipped ?? true);
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof ApiError
              ? `Preview failed (${err.status}): ${err.message}`
              : err instanceof Error
                ? err.message
                : String(err);
          setPreviewError(message);
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [
    workbookId,
    sheetName,
    profile,
    loading,
    overlayReady,
    previewLimit,
    previewNonce,
    overlayForRequest,
  ]);

  // Auto-close the sequence panel when the user changes sheet (URL change
  // unmounts/remounts the page, but this is belt-and-braces) or swaps the
  // active profile. The panel's analysis depends on the profile; rather than
  // try to keep it consistent across a profile swap we just close it.
  useEffect(() => {
    setSequencePanelOpen(false);
  }, [sheetName, loadedProfileId]);

  // ---- rule mutation ---------------------------------------------------
  const currentRule: Rule | undefined = selectedColumn
    ? profile.fields[selectedColumn]
    : undefined;

  function setRule(next: Rule) {
    if (!selectedColumn) return;
    setProfile((p) => ({
      ...p,
      fields: { ...p.fields, [selectedColumn]: next },
    }));
    setDirty(true);
  }

  /**
   * Whole-profile setter — used by the SequencePanel to swap `executionOrder`
   * in one go. Marks the working profile as dirty so the Save button picks up
   * the new order.
   */
  function handleChangeProfile(next: Profile) {
    setProfile(next);
    setDirty(true);
  }

  function rememberKind(key: string, rule: Rule) {
    setKindMemory((m) => ({ ...m, [key]: rule }));
  }

  function clearCurrentRule() {
    if (!selectedColumn) return;
    setProfile((p) => {
      const next = { ...p.fields };
      delete next[selectedColumn];
      return { ...p, fields: next };
    });
    setDirty(true);
  }

  function addCustomColumn(name: string) {
    if (!name) return;
    setCustomColumns((cs) => (cs.includes(name) ? cs : [...cs, name]));
    setProfile((p) => {
      if (p.fields[name]) return p;
      return {
        ...p,
        fields: { ...p.fields, [name]: { kind: "static", value: "" } },
      };
    });
    setDirty(true);
    setSelectedColumn(name);
  }

  // ---- auto-map handler -------------------------------------------------
  const handleAutoMap = useCallback(
    (opts: { overrideExisting: boolean }) => {
      if (!sourceHeaders.length) {
        setAutoMapNotice("No source headers available to auto-map.");
        return;
      }
      const result = autoMapHeaders(SHOPIFY_COLUMNS, sourceHeaders);
      const filled: string[] = [];
      setProfile((p) => {
        const nextFields = { ...p.fields };
        for (const [col, rule] of Object.entries(result.added)) {
          if (!opts.overrideExisting && nextFields[col] !== undefined) continue;
          nextFields[col] = rule;
          filled.push(col);
        }
        return { ...p, fields: nextFields };
      });

      if (filled.length > 0) setDirty(true);

      // Highlight the newly-filled rows briefly.
      setRecentlyAutoMapped(new Set(filled));
      if (autoMapHighlightTimer.current) {
        clearTimeout(autoMapHighlightTimer.current);
      }
      autoMapHighlightTimer.current = setTimeout(() => {
        setRecentlyAutoMapped(new Set());
        autoMapHighlightTimer.current = null;
      }, 1500);

      // Toast-style notice.
      const skipped = result.matched.length - filled.length;
      const notice =
        filled.length === 0
          ? skipped > 0
            ? `Auto-map found ${skipped} candidate${
                skipped === 1 ? "" : "s"
              } but every one already had a rule. Use the dropdown to replace.`
            : "Auto-map didn't find any obvious matches."
          : `Auto-mapped ${filled.length} column${
              filled.length === 1 ? "" : "s"
            } from source headers${
              skipped > 0 ? ` (${skipped} already set)` : ""
            }.`;
      setAutoMapNotice(notice);
      if (autoMapNoticeTimer.current) clearTimeout(autoMapNoticeTimer.current);
      autoMapNoticeTimer.current = setTimeout(() => {
        setAutoMapNotice(null);
        autoMapNoticeTimer.current = null;
      }, 4000);
    },
    [sourceHeaders],
  );

  // Cleanup timers on unmount.
  useEffect(() => {
    return () => {
      if (autoMapHighlightTimer.current) {
        clearTimeout(autoMapHighlightTimer.current);
      }
      if (autoMapNoticeTimer.current) {
        clearTimeout(autoMapNoticeTimer.current);
      }
    };
  }, []);

  // ---- variants helpers -----------------------------------------------
  const variants: VariantConfig = profile.variants ?? {
    enabled: false,
    optionName: "",
    options: [],
  };

  function updateTaxonomy(next: TaxonomyConfig) {
    setProfile((p) => ({ ...p, taxonomy: next }));
    setDirty(true);
  }

  function updateImages(next: ImageConfig) {
    setProfile((p) => ({ ...p, images: next }));
    setDirty(true);
  }

  function updateVariants(next: VariantConfig) {
    setProfile((p) => ({
      ...p,
      variants: next,
    }));
    setDirty(true);
  }

  async function bindProfileToSheet(profileId: string) {
    const binding = await setWorkbookSheetBinding(workbookId, sheetName, profileId);
    setBindings((prev) => {
      const others = prev.filter((b) => b.sheetName !== sheetName);
      return [...others, binding];
    });
  }

  async function refreshProfileList() {
    try {
      const activeCustomerId = readActiveCustomerId();
      const json = await listProfiles({ customerId: activeCustomerId });
      setAvailableProfiles(json ?? []);
    } catch {
      /* non-fatal */
    }
  }

  function openSaveTemplate(mode?: SaveTemplateMode) {
    setSaveModalMode(mode ?? (loadedProfileId ? "overwrite" : "new"));
  }

  /**
   * Save workbook: keep the current rules on THIS sheet (the template is not
   * touched), together with its edits and AI output — like saving a
   * spreadsheet file. Reopening the sheet and exporting use these rules.
   */
  const saveWorkbook = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      setSheetProfile(profile);
      await flushOverlay();
      setSheetOwnRules(true);
      setDirty(false);
      setWorkbookSavedAt(Date.now());
    } catch (err) {
      setSaveError(
        `Could not save the workbook: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setSaving(false);
    }
  }, [saving, profile, setSheetProfile, flushOverlay]);

  // After the rules are saved to the template, the sheet follows the template
  // again (its own copy would only be a stale duplicate).
  async function followTemplate() {
    setSheetProfile(null);
    setSheetOwnRules(false);
    try {
      await flushOverlay();
    } catch (err) {
      console.error("[v2 mapper] clear sheet rules", err);
    }
  }

  // Drop the sheet's own saved rules and go back to the bound template's.
  async function revertToTemplateRules() {
    if (!loadedProfileId) return;
    if (
      !window.confirm(
        "Replace this workbook's own rules with the template's rules? Edits and AI output in the cells are kept.",
      )
    ) {
      return;
    }
    try {
      const template = await getProfile(loadedProfileId);
      applyLoadedProfile(template.config);
      await followTemplate();
      setDirty(false);
    } catch (err) {
      setSaveError(
        `Could not load the template: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  // ⌘S / Ctrl+S saves the workbook, like any spreadsheet.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        void saveWorkbook();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [saveWorkbook]);

  async function createTemplate(args: { name: string; type: string }) {
    if (saving) return;
    setSaving(true);
    try {
      const customerId = readActiveCustomerId();
      const created = await createProfile({
          name: args.name,
          productType: profile.productType,
          config: profile,
          type: args.type || DEFAULT_TEMPLATE_TYPE,
          ...(customerId ? { customerId } : {}),
      });
      setLoadedProfileId(created.id);
      await bindProfileToSheet(created.id);
      await followTemplate();
      await refreshProfileList();
      setDirty(false);
      setSaveModalMode(null);
    } catch (err) {
      console.error("[v2 mapper] create template", err);
      throw err;
    } finally {
      setSaving(false);
    }
  }

  async function overwriteTemplate(profileId: string) {
    if (saving) return;
    setSaving(true);
    try {
      await updateProfile(profileId, { config: profile });
      setLoadedProfileId(profileId);
      await bindProfileToSheet(profileId);
      await followTemplate();
      await refreshProfileList();
      setDirty(false);
      setSaveModalMode(null);
    } catch (err) {
      console.error("[v2 mapper] overwrite template", err);
      throw err;
    } finally {
      setSaving(false);
    }
  }

  async function handleBindCurrent() {
    if (!loadedProfileId) {
      window.alert(
        "Save the template first, then bind it. Use 'Save' or 'Save as new'.",
      );
      return;
    }
    try {
      await bindProfileToSheet(loadedProfileId);
      window.alert("Sheet bound to this template.");
    } catch (err) {
      window.alert(
        `Bind failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  // ---- derived ---------------------------------------------------------
  const detectedHere: Detection | undefined =
    workbookMeta?.detected?.[sheetName];

  // Whether to show the bottom bar's "Generate AI" control. Cheap O(rules)
  // over the active profile.
  const profileHasAIRules = useMemo(
    () => Object.values(profile.fields ?? {}).some((r) => r?.kind === "ai"),
    [profile.fields],
  );

  // AI cells in view that still need generating (stored output doesn't count).
  const pendingAICells = useMemo(() => {
    let count = 0;
    for (const row of previewRows) {
      for (const value of Object.values(row)) {
        if (value === AI_RULE_PLACEHOLDER) count++;
      }
    }
    return count;
  }, [previewRows]);

  // AI failures from the last generation stay listed until the next one.
  const visibleWarnings = useMemo(
    () => [...new Set([...aiWarnings, ...previewWarnings])],
    [aiWarnings, previewWarnings],
  );

  const gridColumns = useGridColumns({ profile, previewColumns, customColumns });
  const workbookColumns = useMemo<GridColumn[]>(() => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const name of gridColumns) {
      if (seen.has(name)) continue;
      seen.add(name);
      names.push(name);
    }
    for (const column of overlay.addedColumns) {
      const name = column.name.trim();
      if (!name || seen.has(name)) continue;
      seen.add(name);
      names.push(name);
    }
    return names.map((name) => ({
      name,
      moduleKind: moduleKindForColumn(profile, overlay, name),
    }));
  }, [gridColumns, overlay, profile]);
  const workbookRows = useMemo<GridRow[]>(() => {
    const makeEmptyRow = (rowId: string): GridRow => {
      const cells: Record<string, string> = {};
      for (const column of workbookColumns) {
        cells[column.name] = String(
          overlay.addedRows[rowId]?.[column.name] ?? "",
        );
      }
      return { rowId, cells };
    };

    const baseRows = previewRows.map((row, index) => {
      const rowId =
        typeof row.rowId === "string" && row.rowId.length > 0
          ? row.rowId
          : stableSourceRowId(index);
      const cells: Record<string, string> = {};
      for (const column of workbookColumns) {
        cells[column.name] = String(row[column.name] ?? "");
      }
      return { rowId, cells };
    });

    const baseById = new Map(baseRows.map((row) => [row.rowId, row]));
    const seen = new Set<string>();
    const ordered: GridRow[] = [];

    const pushRow = (rowId: string) => {
      if (seen.has(rowId)) return;
      const baseRow = baseById.get(rowId);
      if (baseRow) {
        ordered.push(baseRow);
        seen.add(rowId);
        return;
      }
      if (Object.prototype.hasOwnProperty.call(overlay.addedRows, rowId)) {
        ordered.push(makeEmptyRow(rowId));
        seen.add(rowId);
      }
    };

    if (overlay.rowOrder.length > 0) {
      for (const rowId of overlay.rowOrder) pushRow(rowId);
      for (const rowId of Object.keys(overlay.addedRows)) pushRow(rowId);
      // Always surface preview rows that aren't in the saved order yet — e.g.
      // variant / image-fanout rows, or rows from a re-import. Without this they
      // would silently disappear once an overlay order exists.
      for (const row of baseRows) pushRow(row.rowId);
      return ordered;
    }

    for (const row of baseRows) pushRow(row.rowId);
    for (const rowId of Object.keys(overlay.addedRows)) pushRow(rowId);

    return ordered;
  }, [previewRows, workbookColumns, overlay]);

  // ---- render ----------------------------------------------------------
  if (loading || !overlayReady) {
    return (
      <div className="flex h-[calc(100vh-3rem)] items-center justify-center bg-slate-50 dark:bg-background text-[13px] text-slate-500 dark:text-slate-400">
        Loading mapper…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex h-[calc(100vh-3rem)] items-center justify-center bg-slate-50 dark:bg-background px-6 text-center text-[13px] text-red-700 dark:text-red-300">
        <div>
          <div className="font-semibold">Could not load mapper</div>
          <div className="mt-1 text-[12px]">{loadError}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-3rem)] min-h-0 flex-col bg-slate-50 dark:bg-background text-[13px] text-slate-900 dark:text-slate-100">
      {/* ---------- Top bar ---------- */}
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-slate-200 dark:border-border bg-white dark:bg-surface px-4 py-1.5">
        <nav className="flex min-w-0 flex-1 items-center gap-1.5 text-[12px] text-slate-500 dark:text-slate-400">
          <a href="/" className="hover:text-slate-900 dark:hover:text-slate-100">
            Dashboard
          </a>
          <span className="text-slate-300 dark:text-slate-600">/</span>
          <a href="/workbooks" className="hover:text-slate-900 dark:hover:text-slate-100">
            Workbooks
          </a>
          <span className="text-slate-300 dark:text-slate-600">/</span>
          <a
            href={`/workbooks/${workbookId}`}
            className="truncate hover:text-slate-900 dark:hover:text-slate-100"
            title={workbookMeta?.originalName ?? workbookId}
          >
            {workbookMeta?.originalName ?? workbookId}
          </a>
          <span className="text-slate-300 dark:text-slate-600">/</span>
          <span
            className="truncate font-medium text-slate-900 dark:text-slate-100"
            title={sheetName}
          >
            {sheetName}
          </span>
          {detectedHere && (
            <span
              className={[
                "ml-2 rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide",
                confidenceTone(detectedHere.confidence),
              ].join(" ")}
              title={`Detected ${detectedHere.type} @ ${Math.round(
                detectedHere.confidence * 100,
              )}%`}
            >
              {detectedHere.type} · {Math.round(detectedHere.confidence * 100)}%
            </span>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <ImportDialog
            workbookId={workbookId}
            sheet={sheetName}
            onImported={() => setReloadNonce((value) => value + 1)}
          />
          <ReconfigureImportButton
            workbookId={workbookId}
            sheetName={sheetName}
            currentConfig={
              workbookMeta?.sheets.find((s) => s.name === sheetName)
                ?.importConfig ?? null
            }
            hasOriginalFile={!!workbookMeta?.originalFileKey}
            onReconfigured={() => setReloadNonce((value) => value + 1)}
          />
          {dirty ? (
            <span
              className="rounded-full bg-amber-100 dark:bg-amber-950/50 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300"
              title="Rule changes aren't saved yet. Save keeps them with this workbook (⌘S); cell edits and AI output save automatically."
            >
              Unsaved
            </span>
          ) : workbookSavedAt ? (
            <span className="text-[11px] text-emerald-700 dark:text-emerald-300">
              ✓ Workbook saved
            </span>
          ) : null}
          {sheetOwnRules && (
            <span
              className="rounded-full bg-sky-50 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-sky-800 ring-1 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800"
              title="This sheet uses its own saved rules. Its template is not changed; use Save › Save template to update it."
            >
              Workbook rules
            </span>
          )}
          <button
            type="button"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-pressed={sidebarOpen}
            title="Show all output fields (the module catalog)"
            className={[
              "inline-flex items-center gap-1 rounded border px-2 py-1.5 text-[12px] font-medium transition",
              sidebarOpen
                ? "border-slate-400 dark:border-slate-600 bg-slate-100 dark:bg-surface-2 text-slate-900 dark:text-slate-100"
                : "border-slate-300 dark:border-border bg-white dark:bg-surface text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-surface-2",
            ].join(" ")}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              width="13"
              height="13"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
              <path d="M6 2.5v11" />
            </svg>
            Show All Fields
          </button>
          <button
            type="button"
            onClick={() => setSequencePanelOpen((o) => !o)}
            aria-pressed={sequencePanelOpen}
            title="Show rule run order"
            className={[
              "inline-flex items-center gap-1 rounded border px-2 py-1.5 text-[12px] font-medium transition",
              sequencePanelOpen
                ? "border-violet-300 bg-violet-50 dark:bg-violet-950/40 text-violet-800 dark:text-violet-300"
                : "border-slate-300 dark:border-border bg-white dark:bg-surface text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-surface-2",
            ].join(" ")}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              width="13"
              height="13"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2.5 3.5h2" />
              <path d="M6.5 3.5h7" />
              <path d="M2.5 8h2" />
              <path d="M6.5 8h7" />
              <path d="M2.5 12.5h2" />
              <path d="M6.5 12.5h7" />
            </svg>
            Run Order
          </button>
          <button
            type="button"
            onClick={() => setVariantsOpen((v) => !v)}
            aria-pressed={variantsOpen}
            title="Variation Expander, generate one row per variant (size / stitched / etc.)"
            className={[
              "inline-flex items-center gap-1 rounded border px-2 py-1.5 text-[12px] font-medium transition",
              variantsOpen
                ? "border-violet-300 bg-violet-50 dark:bg-violet-950/40 text-violet-800 dark:text-violet-300"
                : "border-slate-300 dark:border-border bg-white dark:bg-surface text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-surface-2",
            ].join(" ")}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              width="13"
              height="13"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 3h4v4H3zM9 9h4v4H9z" />
              <path d="M7 5h4v4" />
            </svg>
            Variants
          </button>
          <button
            type="button"
            onClick={() => setImagesOpen((v) => !v)}
            aria-pressed={imagesOpen}
            title="Images, pick image columns in order + alt text; each extra image becomes its own Shopify row"
            className={[
              "inline-flex items-center gap-1 rounded border px-2 py-1.5 text-[12px] font-medium transition",
              imagesOpen
                ? "border-teal-300 bg-teal-50 dark:bg-teal-950/40 text-teal-800 dark:text-teal-300"
                : "border-slate-300 dark:border-border bg-white dark:bg-surface text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-surface-2",
            ].join(" ")}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              width="13"
              height="13"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="3" width="12" height="10" rx="1.5" />
              <path d="M2.5 11l3.5-3.5 3 3 2-2 2.5 2.5" />
              <circle cx="10.5" cy="6" r="1" />
            </svg>
            Images
          </button>
          <button
            type="button"
            onClick={() => setTaxonomyOpen((t) => !t)}
            aria-pressed={taxonomyOpen}
            title="Category, pick the product category + attribute columns"
            className={[
              "inline-flex items-center gap-1 rounded border px-2 py-1.5 text-[12px] font-medium transition",
              taxonomyOpen
                ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300"
                : "border-slate-300 dark:border-border bg-white dark:bg-surface text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-surface-2",
            ].join(" ")}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              width="13"
              height="13"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2.5 4.5l5.5-2 5.5 2-5.5 2z" />
              <path d="M2.5 8l5.5 2 5.5-2M2.5 11.5l5.5 2 5.5-2" />
            </svg>
            Taxonomy
          </button>
          <SaveMenu
            saving={saving}
            templateName={
              availableProfiles.find((p) => p.id === loadedProfileId)?.name ?? null
            }
            sheetOwnRules={sheetOwnRules}
            onSaveWorkbook={() => void saveWorkbook()}
            onSaveTemplate={() => openSaveTemplate()}
            onSaveAsNewTemplate={() => openSaveTemplate("new")}
            onBindTemplate={() => void handleBindCurrent()}
            onUseTemplateRules={() => void revertToTemplateRules()}
          />
          <button
            type="button"
            onClick={() => exportWorkbook()}
            disabled={exportStatus === "running"}
            title="Export this workbook to a Shopify-importable CSV"
            className={[
              "inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-sm transition disabled:cursor-not-allowed",
              exportStatus === "error"
                ? "bg-red-600 hover:bg-red-600"
                : "bg-emerald-600 hover:bg-emerald-500 disabled:opacity-90",
            ].join(" ")}
          >
            {exportStatus === "running" ? (
              <>
                <svg
                  aria-hidden="true"
                  className="animate-spin"
                  viewBox="0 0 16 16"
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M8 1.5a6.5 6.5 0 1 1-6.5 6.5" opacity="0.9" />
                </svg>
                Exporting… {exportProgress}%
              </>
            ) : exportStatus === "done" ? (
              <>✓ Downloaded</>
            ) : exportStatus === "error" ? (
              <>Export failed, retry</>
            ) : (
              <>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16"
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M8 2v8" />
                  <path d="M4.5 7l3.5 3.5L11.5 7" />
                  <path d="M2.5 13.5h11" />
                </svg>
                Export CSV
              </>
            )}
          </button>
        </div>
      </div>

      {/* ---------- Body: spreadsheet canvas + floating panels ---------- */}
      <div className="relative flex min-h-0 flex-1">
        <main className="flex min-w-0 flex-1 flex-col bg-white dark:bg-surface">
          {taxonomyOpen && (
            <TaxonomyPanel
              taxonomy={profile.taxonomy}
              sourceHeaders={sourceHeaders}
              onChange={updateTaxonomy}
              onClose={() => setTaxonomyOpen(false)}
            />
          )}

          {imagesOpen && (
            <section className="max-h-[55vh] shrink-0 overflow-y-auto border-b border-slate-200 dark:border-border bg-slate-50 dark:bg-background px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Images
                </div>
                <button
                  type="button"
                  onClick={() => setImagesOpen(false)}
                  className="rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-2 py-1 text-[11px] text-slate-600 dark:text-slate-400 transition hover:bg-slate-100 dark:hover:bg-surface-2 hover:text-slate-900 dark:hover:text-slate-100"
                >
                  Close
                </button>
              </div>
              <div className="mt-2 max-w-3xl">
                <ImagesEditor
                  images={profile.images}
                  onChange={updateImages}
                  sourceHeaders={sourceHeaders}
                  outputColumns={Object.keys(profile.fields)}
                  sampleRow={sourceRows[0]}
                  sampleOutput={previewRows[0]}
                  registerFocusedAutocomplete={registerFocusedAutocomplete}
                />
              </div>
            </section>
          )}

          {variantsOpen && (
            <LayeredVariantsPanel
              variants={variants}
              onChange={updateVariants}
              onClose={() => setVariantsOpen(false)}
              applyToOptions={[...shopifyTarget.priceLikeColumns, ...shopifyTarget.skuLikeColumns]}
            />
          )}

          <div className="min-h-0 flex-1">
            {bottomTab === "source" ? (
              editSheetError ? (
                <div className="flex h-full items-center justify-center bg-white px-6 text-center text-[13px] text-red-700 dark:bg-surface dark:text-red-300">
                  <div>
                    <div className="font-semibold">
                      Could not load source for editing
                    </div>
                    <div className="mt-1 text-[12px]">{editSheetError}</div>
                    <button
                      type="button"
                      onClick={() => void loadEditSheet()}
                      className="mt-3 rounded border border-slate-300 bg-white px-3 py-1 text-[12px] font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              ) : editSheet ? (
                <EditableSourceGrid
                  key={`${sheetName}:${reloadNonce}`}
                  workbookId={workbookId}
                  sheetName={sheetName}
                  initialHeaders={editSheet.headers}
                  initialRows={editSheet.rows}
                  initialTotal={editSheet.total}
                  onInsertColumn={insertTokenIntoInspector}
                  onSaved={handleSourceSaved}
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-white text-[13px] text-slate-500 dark:bg-surface dark:text-slate-400">
                  Loading source…
                </div>
              )
            ) : (
              <WorkbookGrid
                columns={workbookColumns}
                rows={workbookRows}
                overlay={overlay}
                setOverride={setOverride}
                resetCell={resetCell}
                selectedColumn={selectedColumn}
                loading={previewLoading || overlaySaving}
                onSelectColumn={setSelectedColumn}
                onGenerate={handlePlayColumn}
                playingColumn={playingColumn}
                onClearGenerated={handleClearGenerated}
                onAddRow={() => {
                  addRow(workbookRows.map((row) => row.rowId));
                }}
                onAddColumn={(name) => {
                  addColumn(name);
                  setSelectedColumn(name.trim());
                }}
                onRemoveRows={(rowIds) => {
                  removeRows(
                    rowIds,
                    workbookRows.map((row) => row.rowId),
                  );
                }}
                onResetColumn={(column) => {
                  setSelectedColumn(column);
                  setProfile((p) => {
                    const next = { ...p.fields };
                    delete next[column];
                    return { ...p, fields: next };
                  });
                  setDirty(true);
                }}
              />
            )}
          </div>
        </main>

        <FieldsPanel
          sourceColumns={sourceHeaders}
          exportColumns={workbookColumns.map((column) => column.name)}
          onInsert={insertTokenIntoInspector}
          open={fieldsPanelOpen}
          onToggle={setFieldsPanelOpen}
        />

        {/* Floating left module catalog (auto-map + add fields). Hidden by
            default; the "Process Sequence" top-bar button toggles it so the
            sheet stays full-width. */}
        {sidebarOpen && (
          <>
            <button
              type="button"
              aria-label="Close column catalog"
              onClick={() => setSidebarOpen(false)}
              className="absolute inset-0 z-20 cursor-default bg-slate-900/10 dark:bg-background/50"
            />
            <div className="absolute left-0 top-0 bottom-0 z-30 flex shadow-2xl">
              <MapperSidebar
                profile={profile}
                selectedColumn={selectedColumn}
                onSelect={(c) => setSelectedColumn(c)}
                customColumns={customColumns}
                onAddCustomColumn={addCustomColumn}
                onAutoMap={handleAutoMap}
                recentlyAutoMapped={recentlyAutoMapped}
              />
            </div>
          </>
        )}

        {/* Floating right editor — opens when a column header/cell is clicked. */}
        {selectedColumn && (
          <ModuleConfigDrawer
            key={selectedColumn}
            column={selectedColumn}
            rule={currentRule}
            productType={profile.productType}
            sourceHeaders={sourceHeaders}
            sourceRows={sourceRows}
            previewRows={previewRows}
            previewLoading={previewLoading}
            profile={profile}
            onChangeRule={setRule}
            onClearRule={clearCurrentRule}
            kindMemory={kindMemory}
            onRememberKind={rememberKind}
            registerFocusedAutocomplete={registerFocusedAutocomplete}
            onSelectColumn={setSelectedColumn}
            onClose={() => setSelectedColumn(null)}
            onSave={() => void saveWorkbook()}
            onSaveTemplate={() => openSaveTemplate()}
            saving={saving}
            dirty={dirty}
            taxonomy={profile.taxonomy}
            onChangeTaxonomy={updateTaxonomy}
            dictionaries={dictionaryMap}
            onDictionariesChanged={loadDictionaries}
            onChangeImages={updateImages}
            rightOffset={
              fieldsPanelOpen ? FIELDS_PANEL_OPEN_WIDTH : FIELDS_PANEL_RAIL_WIDTH
            }
            exportColumns={workbookColumns.map((column) => column.name)}
            onInsertField={insertTokenIntoInspector}
          />
        )}
      </div>

      {autoMapNotice && (
        <div
          role="status"
          className="pointer-events-none fixed bottom-6 left-1/2 z-40 -translate-x-1/2 rounded-md bg-slate-900 dark:bg-slate-100 dark:text-slate-950 px-3 py-1.5 text-[12px] font-medium text-white shadow-lg"
        >
          {autoMapNotice}
        </div>
      )}

      {previewError && (
        <div className="shrink-0 border-t border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/40 px-4 py-1.5 text-[11.5px] text-red-700 dark:text-red-300">
          {previewError}
        </div>
      )}
      {saveError && (
        <div className="shrink-0 border-t border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/40 px-4 py-1.5 text-[11.5px] text-red-700 dark:text-red-300">
          {saveError}
        </div>
      )}
      <WarningsBar warnings={visibleWarnings} />

      {/* ---------- Bottom bar: sheet tabs + controls (Sheets-style) ---------- */}
      <GridBottomBar
        activeTab={bottomTab}
        onSelectTab={setBottomTab}
        previewLoading={previewLoading}
        previewCount={previewRows.length}
        sourceCount={sourceRows.length}
        sourceTotal={sourceTotal}
        previewLimit={previewLimit}
        onChangePreviewLimit={handleChangePreviewLimit}
        aiSkipped={lastAISkipped}
        pendingAICells={pendingAICells}
        profileHasAIRules={profileHasAIRules}
        onTestAI={handleTestAI}
        testingAI={testingAI}
        testedAt={testedAt}
        testedCount={testedCount}
      />

      {/* ---------- Sequence slide-over ---------- */}
      <SequencePanel
        open={sequencePanelOpen}
        profile={profile}
        onClose={() => setSequencePanelOpen(false)}
        onChangeProfile={handleChangeProfile}
        onSelectColumn={(col) => setSelectedColumn(col)}
      />

      <SaveTemplateModal
        open={saveModalMode !== null}
        initialMode={saveModalMode ?? (loadedProfileId ? "overwrite" : "new")}
        profile={profile}
        profiles={availableProfiles}
        loadedProfileId={loadedProfileId}
        saving={saving}
        onClose={() => setSaveModalMode(null)}
        onCreate={createTemplate}
        onOverwrite={overwriteTemplate}
      />
    </div>
  );
}
